import { addMonths, type MonthKey } from '../format';
import { toWesternDigits } from '../text';

/**
 * The new-prices form. The committee types where each tier ends and its rate;
 * where it starts is derived (1 for the first, previous end + 1 after), and
 * the last tier is always open. So the tiers are contiguous, start at 1 and
 * end open by construction, the rules arqam enforces (docs/changes/34 there).
 */
export type BillingMethod = 'PROGRESSIVE' | 'FIXED';

export type FormTier = {
  /** Last m³ of the tier, as typed; ignored for the last tier (open). */
  end: string;
  rate: string;
  description: string;
};

export type FormFee = {
  name: string;
  feeType: 'FIXED' | 'PERCENTAGE';
  amount: string;
  onlyWhenNoConsumption: boolean;
  description: string;
};

export type ConfigForm = {
  name: string;
  description: string;
  /** "YYYY-MM": the configuration starts on the 1st of this month. */
  month: MonthKey;
  billingMethod: BillingMethod;
  tiers: FormTier[];
  fees: FormFee[];
  /** Shart price per person and month (arqam doc 45), as typed. */
  shartUnitPrice: string;
};

/** "4", "4,5", "٤٫٥" → "4.50"; null when not a number ≥ 0 with ≤ 2 decimals. */
export function parseMoney(input: string): string | null {
  const value = toWesternDigits(input).trim().replace(/[,٫]/g, '.');
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return null;
  const [int, dec = ''] = value.split('.');
  return `${Number(int)}.${dec.padEnd(2, '0')}`;
}

const parseCount = (input: string): number | null => {
  const value = toWesternDigits(input).trim();
  return /^\d+$/.test(value) ? Number(value) : null;
};

/** Where each tier starts, from the ends typed before it. */
export function tierStarts(tiers: FormTier[]): (number | null)[] {
  const starts: (number | null)[] = [];
  let next: number | null = 1;
  for (const tier of tiers) {
    starts.push(next);
    const end = parseCount(tier.end);
    next = next !== null && end !== null ? end + 1 : null;
  }
  return starts;
}

export type FormProblem = { path: string; message: string };

/** What the committee can fix before asking arqam (arqam checks again). */
export function checkForm(form: ConfigForm): FormProblem[] {
  const problems: FormProblem[] = [];
  if (!form.name.trim()) problems.push({ path: 'name', message: 'اكتب اسماً.' });
  if (form.name.length > 255) {
    problems.push({ path: 'name', message: 'الاسم طويل جداً.' });
  }
  const tiers =
    form.billingMethod === 'FIXED' ? form.tiers.slice(0, 1) : form.tiers;
  const starts = tierStarts(tiers);
  tiers.forEach((tier, i) => {
    const last = i === tiers.length - 1;
    if (parseMoney(tier.rate) === null) {
      problems.push({
        path: `tiers.${i}.rate`,
        message: 'سعر غير صحيح، مثلاً 4 أو 4,50.',
      });
    }
    if (!last) {
      const end = parseCount(tier.end);
      const start = starts[i];
      if (end === null) {
        problems.push({
          path: `tiers.${i}.end`,
          message: 'اكتب نهاية الشريحة (م³).',
        });
      } else if (start !== null && end < start) {
        problems.push({
          path: `tiers.${i}.end`,
          message: `النهاية لا تكون أصغر من البداية (${start}).`,
        });
      } else if (end > 32766) {
        problems.push({ path: `tiers.${i}.end`, message: 'رقم كبير جداً.' });
      }
    }
  });
  const names = new Set<string>();
  form.fees.forEach((fee, i) => {
    const key = fee.name.trim().replace(/\s+/g, ' ').toLowerCase();
    if (!key) {
      problems.push({ path: `fees.${i}.name`, message: 'اكتب اسم الرسم.' });
    } else if (names.has(key)) {
      problems.push({ path: `fees.${i}.name`, message: 'اسم مكرر.' });
    }
    names.add(key);
    const amount = parseMoney(fee.amount);
    if (amount === null) {
      problems.push({ path: `fees.${i}.amount`, message: 'مبلغ غير صحيح.' });
    } else if (fee.feeType === 'PERCENTAGE' && Number(amount) > 100) {
      problems.push({
        path: `fees.${i}.amount`,
        message: 'النسبة لا تتجاوز 100.',
      });
    }
  });
  if (parseMoney(form.shartUnitPrice) === null) {
    problems.push({
      path: 'shartUnitPrice',
      message: 'ثمن غير صحيح، مثلاً 18 أو 18,50 (لا يكون سالباً).',
    });
  }
  return problems;
}

/** The typed inputs arqam expects (BillingTierInput / BillingFeeInput). */
export function toApiInput(form: ConfigForm) {
  const tiers =
    form.billingMethod === 'FIXED' ? form.tiers.slice(0, 1) : form.tiers;
  const starts = tierStarts(tiers);
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    effectiveFrom: `${form.month}-01`,
    billingMethod: form.billingMethod,
    tiers: tiers.map((tier, i) => {
      const last = i === tiers.length - 1;
      return {
        tierStart: starts[i] ?? 1,
        tierEnd: last ? null : parseCount(tier.end),
        rate: parseMoney(tier.rate) ?? tier.rate,
        description: tier.description.trim() || null,
      };
    }),
    fees: form.fees.map((fee) => ({
      name: fee.name.trim(),
      feeType: fee.feeType,
      amount: parseMoney(fee.amount) ?? fee.amount,
      onlyWhenNoConsumption: fee.onlyWhenNoConsumption,
      description: fee.description.trim() || null,
    })),
    shartUnitPrice: parseMoney(form.shartUnitPrice) ?? form.shartUnitPrice,
  };
}

type SavedConfig = {
  billingMethod: BillingMethod;
  effectiveFrom: string;
  tiers: { tierEnd: number | null; rate: string; description: string | null }[];
  fees: {
    name: string;
    feeType: 'FIXED' | 'PERCENTAGE';
    amount: string;
    onlyWhenNoConsumption: boolean;
    description: string | null;
  }[];
};

/**
 * A new form starting from the latest configuration's prices, and from the
 * current configuration's shart price (the one bills use today).
 */
type Starting = SavedConfig & { shartUnitPrice?: string | null };

/**
 * A new form starting from the latest configuration's prices, the month after
 * both the latest configuration's start and `current`. (Old configurations
 * start their first tier at 0; the form always starts at 1.)
 */
export function formFrom(
  latest: Starting | null,
  current: MonthKey,
  currentShartUnitPrice?: string | null,
): ConfigForm {
  const shart = currentShartUnitPrice ?? latest?.shartUnitPrice ?? null;
  const after = latest
    ? addMonths(latest.effectiveFrom.slice(0, 7) as MonthKey, 1)
    : current;
  const next = addMonths(current, 1);
  return {
    name: '',
    description: '',
    month: after > next ? after : next,
    billingMethod: latest?.billingMethod ?? 'PROGRESSIVE',
    tiers: latest?.tiers.length
      ? latest.tiers.map((t) => ({
          end: t.tierEnd === null ? '' : String(t.tierEnd),
          rate: String(Number(t.rate)),
          description: t.description ?? '',
        }))
      : [{ end: '', rate: '', description: '' }],
    fees: (latest?.fees ?? []).map((f) => ({
      name: f.name,
      feeType: f.feeType,
      amount: String(Number(f.amount)),
      onlyWhenNoConsumption: f.onlyWhenNoConsumption,
      description: f.description ?? '',
    })),
    shartUnitPrice: shart === null ? '' : String(Number(shart)),
  };
}

/** The bills shown in the comparison (m³). */
export const SAMPLE_CONSUMPTIONS = [0, 5, 10, 20, 30, 50];
